import React, { useState, useEffect } from "react";
import { useRole } from "@/hooks/useRole";
import { usePlayerProfile } from "@/hooks/usePlayerProfile";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { ImagesCard } from "@/components/profile/ImagesCard";
import { ProfileHeader } from "@/components/player/profile/ProfileHeader";
import { ProfileBasicInfo } from "@/components/player/profile/ProfileBasicInfo";
import { ProfileSocialMedia } from "@/components/player/profile/ProfileSocialMedia";
import { ProfileActions } from "@/components/player/profile/ProfileActions";
import { BecomeMentorCard } from "@/components/player/BecomeMentorCard";

const Perfil = () => {
  const { user, role } = useRole();
  const { 
    profile, 
    socialMedia, 
    loading, 
    updateProfile, 
    updateSocialMedia,
    uploadProfileImage,
    removeProfileImage
  } = usePlayerProfile();
  
  const [nickname, setNickname] = useState("");
  const [preferredGame, setPreferredGame] = useState("");
  const [biography, setBiography] = useState("");
  const [instagram, setInstagram] = useState("");
  const [discord, setDiscord] = useState("");
  const [twitch, setTwitch] = useState("");
  const [youtube, setYoutube] = useState("");
  
  const [isSaving, setIsSaving] = useState(false);

  // Update form fields when data is loaded - priorizar dados do profile
  useEffect(() => {
    console.log('🔄 [Perfil] Atualizando campos do formulário...');
    console.log('📊 [Perfil] Profile data:', profile);
    console.log('👤 [Perfil] User data:', user);
    
    // Priorizar dados do profile (mais específico) sobre user data
    if (profile) {
      if (profile.nickname) {
        console.log('✅ [Perfil] Definindo nickname do profile:', profile.nickname);
        setNickname(profile.nickname);
      } else if (user?.nickname) {
        console.log('🔄 [Perfil] Usando nickname do user como fallback:', user.nickname);
        setNickname(user.nickname);
      }
      
      if (profile.preferred_game) {
        console.log('✅ [Perfil] Definindo preferred_game do profile:', profile.preferred_game);
        setPreferredGame(profile.preferred_game);
      } else if (user?.preferred_game) {
        console.log('🔄 [Perfil] Usando preferred_game do user como fallback:', user.preferred_game);
        setPreferredGame(user.preferred_game);
      }
    } else if (user) {
      // Se não temos profile data ainda, usar user data temporariamente
      if (user.nickname && !nickname) {
        console.log('🔄 [Perfil] Usando nickname do user (temporário):', user.nickname);
        setNickname(user.nickname);
      }
      if (user.preferred_game && !preferredGame) {
        console.log('🔄 [Perfil] Usando preferred_game do user (temporário):', user.preferred_game);
        setPreferredGame(user.preferred_game);
      }
    }
  }, [user, profile]);

  useEffect(() => {
    console.log('🔄 [Perfil] Atualizando dados de social media...');
    console.log('📊 [Perfil] Social media data:', socialMedia);
    
    if (socialMedia) {
      setBiography(socialMedia.biography || "");
      setInstagram(socialMedia.instagram_url || "");
      setDiscord(socialMedia.discord_url || "");
      setTwitch(socialMedia.twitch_url || "");
      setYoutube(socialMedia.youtube_url || "");
    }
  }, [socialMedia]);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      // Update profile
      await updateProfile({
        nickname,
        preferred_game: preferredGame
      });
      
      // Update social media
      await updateSocialMedia({
        biography,
        instagram_url: instagram,
        discord_url: discord,
        twitch_url: twitch,
        youtube_url: youtube
      });
      
      toast({
        title: "Perfil Atualizado",
        description: "Suas informações foram salvas com sucesso!",
      });
    } catch (error) {
      console.error("Error saving profile:", error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao salvar o perfil. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleProfileImageUpload = async (file: File) => {
    try {
      await uploadProfileImage(file);
      toast({
        title: "Imagem de perfil atualizada",
        description: "Sua foto de perfil foi atualizada com sucesso!",
      });
    } catch (error) {
      console.error("Error uploading profile image:", error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao fazer upload da imagem.",
        variant: "destructive"
      });
    }
  };

  const handleProfileImageRemove = async () => {
    try {
      await removeProfileImage();
      toast({
        title: "Imagem removida",
        description: "Sua foto de perfil foi removida com sucesso!",
      });
    } catch (error) {
      console.error("Error removing profile image:", error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao remover a imagem.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <ProfileHeader 
        title="Seu Perfil" 
        subtitle="Gerencie suas informações pessoais e preferências" 
      />

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          {/* Basic Info */}
          <ProfileBasicInfo 
            nickname={nickname}
            preferredGame={preferredGame}
            setNickname={setNickname}
            setPreferredGame={setPreferredGame}
          />

          {/* Social Media */}
          <ProfileSocialMedia 
            biography={biography}
            instagram={instagram}
            discord={discord}
            twitch={twitch}
            youtube={youtube}
            setBiography={setBiography}
            setInstagram={setInstagram}
            setDiscord={setDiscord}
            setTwitch={setTwitch}
            setYoutube={setYoutube}
          />

          {/* Become Mentor Card - Mostrar apenas se for jogador */}
          {role === 'jogador' && (
            <BecomeMentorCard />
          )}
        </div>

        {/* Profile Images Section */}
        <div className="md:col-span-1">
          <ImagesCard 
            profileImageUrl={socialMedia?.image_url || null}
            onProfileImageUpload={handleProfileImageUpload}
            onProfileImageRemove={handleProfileImageRemove}
            showCoverImage={false}
          />
          
          <ProfileActions 
            isSaving={isSaving} 
            onSave={handleSaveProfile} 
          />
        </div>
      </div>
    </div>
  );
};

export default Perfil;
